import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, FileText, MessageSquareText, Plus, ShieldCheck } from "lucide-react";
import { requestSummaryJump } from "../summary-turns.js";
import type { AgentProposal, BoardSnapshot, OpsAssistantWorkspaceReference, OpsPublicProposalProjection, OpsSha256, WorkItem, WorkStatus } from "@business-ops/domain";
import { api } from "../api.js";
import { WhatsAppChatStatus } from "../components/whatsapp-chat-status.js";
import type { OpsWorkspaceMode } from "../components/shell.js";
import { OpsAssistantPanel, type OpsWorkspaceReferenceSummary } from "../components/ops-assistant.js";
import { MeetingCapture } from "../components/meeting-capture.js";
import { WorkspaceToolbar, readSideChatCollapsed, useDesktopWorkspace, writeSideChatCollapsed } from "../components/workspace-toolbar.js";
import { useOpsMediaOwner } from "../components/ops-media-owner.js";
import { memberName } from "../helpers.js";
import { subscribeBoardRefreshHints, type BoardRefreshHint } from "../use-board.js";
import {
  applyWorkMutationFeedbackUpdate,
  observeWorkMutationFeedback,
  type WorkMutationFeedback,
  type WorkMutationFeedbackUpdate
} from "../work-mutation-feedback.js";
import {
  ApprovalUnitCard,
  ApprovalUnitOutcomeCard,
  PublicProposalTerminalReceiptUnit,
  ProposalDraftStateProvider,
  approvalUnits,
  hasTerminalPublicEffectReceipt,
  proposalAttentionKind,
  type AgentWorkQueue,
  type ApprovalUnit,
  type ProposalOutcomeActions
} from "./review.js";
import { CalendarView, calendarEvents, calendarEventsForDay, calendarViewContextForDay, defaultCalendarViewContext, type CalendarViewContext } from "./secondary.js";
import { DEFAULT_WORK_VIEW_STATE, WorkView, filteredWorkItems, type WorkLens, type WorkViewState } from "./work.js";

export const OPS_PROPOSAL_TRANSITION_STATUS = {
  title: "Checking outcome.",
  detail: "Waiting for the final safety result."
} as const;

export function OpsAgentView({ board, queue, historyScope, publicReleaseId, privateMode, onVoicePresentationPrivacyLockChange, canArchiveWork, mode, setMode, openWork, moveWork, addWork, addWorkForDay, openConversations, mobileChatOpen = false, openMobileChat, whatsAppConnectionStatus, openWhatsApp }: {
  board: BoardSnapshot;
  whatsAppConnectionStatus?: string | null;
  openWhatsApp?: () => void;
  queue: AgentWorkQueue;
  historyScope: string;
  publicReleaseId: OpsSha256;
  privateMode: boolean;
  onVoicePresentationPrivacyLockChange?: (locked: boolean) => void;
  canArchiveWork: boolean;
  displayName: string;
  mode: OpsWorkspaceMode;
  setMode: (mode: OpsWorkspaceMode) => void;
  openWork: (item: WorkItem) => void;
  moveWork: (item: WorkItem, status: WorkStatus) => Promise<void>;
  addWork: () => void;
  addWorkForDay: (dayKey: string) => void;
  openConversations?: ((sourceEventId?: string) => void) | undefined;
  mobileChatOpen?: boolean;
  openMobileChat?: () => void;
}) {
  const mediaOwner = useOpsMediaOwner();
  const workspaceRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const ordered = useMemo(() => orderAgentProposalUnits(queue.proposals), [queue.proposals]);
  const allUnits = useMemo(
    () => approvalUnits([...queue.proposals, ...(queue.history ?? [])]),
    [queue.history, queue.proposals]
  );
  const pendingUnitByProposalId = useMemo(() => approvalUnitByProposalId(ordered), [ordered]);
  const currentReviewProposalIds = useMemo(
    () => new Set(pendingUnitByProposalId.keys()),
    [pendingUnitByProposalId]
  );
  const unmaterializedWorkProposalIds = useMemo(() => new Set(queue.proposals
    .filter((proposal) => proposal.state === "READY_FOR_REVIEW"
      && proposal.effect.kind === "NEW_WORK"
      && proposal.workItemId === null
      && !board.workItems.some((item) => item.id === proposal.id))
    .map((proposal) => proposal.id)), [board.workItems, queue.proposals]);
  const currentWorkItemIds = useMemo(
    () => new Set(board.workItems.map((item) => item.id)),
    [board.workItems]
  );
  const historyUnitByProposalId = useMemo(
    () => approvalUnitByProposalId(approvalUnits(queue.history ?? [])),
    [queue.history]
  );
  // WHY [BOL-INLINE-BUNDLE-TERMINAL-001]: local queue removal precedes the
  // next history projection. Remember the server-declared member set for this
  // mounted workspace so that brief bridge cannot split one finished inline
  // unit into two identical “Checking outcome” cards. Falsifier: approving an
  // inline bundle flashes one status per proposal before history refreshes.
  const knownBundleGroupsRef = useRef(new Map<string, { id: string; proposalIds: readonly string[] }>());
  for (const unit of allUnits) {
    if (unit.bundleId) {
      knownBundleGroupsRef.current.set(unit.bundleId, {
        id: unit.id,
        proposalIds: unit.proposals.map((proposal) => proposal.id)
      });
    }
  }
  const proposalRenderGroups = [...knownBundleGroupsRef.current.values()];
  const [selectedApprovalId, setSelectedApprovalId] = useState<string | null>(ordered[0]?.id ?? null);
  const [calendarContext, setCalendarContext] = useState<CalendarViewContext>(() => defaultCalendarViewContext(board.workspace.timezone));
  const [calendarSurfaceRevision, setCalendarSurfaceRevision] = useState(0);
  // WHY [BOL-CHAT-FIRST-TASKS-RETURN-001]: tool mode may unmount its visual
  // surface, but Chat and the operator's exact Tasks context are one journey.
  // Keep lens/filter/lane and split ownership above WorkView; never duplicate
  // Work records in chat state. Falsifier: Back loses chat/draft or Tasks state.
  const [workViewState, setWorkViewState] = useState<WorkViewState>(DEFAULT_WORK_VIEW_STATE);
  const [workMutationFeedback, setWorkMutationFeedback] = useState<readonly WorkMutationFeedback[]>([]);
  const [workspaceWidth, setWorkspaceWidth] = useState(68);
  // WHY [BOL-APP-WORKSPACE-TOOLBAR-001]: one toolbar row per view (R785). Tasks portals its own lens/search
  // controls into this slot on desktop, and split views may fold the side chat away (default open).
  const [toolbarSlot, setToolbarSlot] = useState<HTMLDivElement | null>(null);
  const desktopWorkspace = useDesktopWorkspace();
  const [sideChatCollapsed, setSideChatCollapsedState] = useState(readSideChatCollapsed);
  const setSideChatCollapsed = (collapsed: boolean) => {
    setSideChatCollapsedState(collapsed);
    writeSideChatCollapsed(collapsed);
  };
  // WHY [BOL-MOBILE-CHAT-DEFAULT-001]: the history flag distinguishes an
  // Ask-Agent overlay from its still-mounted tool; it is false on a fresh Ops
  // Chat entry too. Treating every false flag as "tool visible" hid the thread
  // and composer while leaving only Today's summary. Keep canonical Chat
  // focused by default, while actual tools remain full-screen on phones.
  // Falsifier: fresh mobile Ops Chat is blank below its summary, or a tool and
  // Chat compete for the same phone viewport.
  const chatFocused = mobileChatOpen || mode === "today";
  const visibleCalendarWorkItemIds = useMemo(
    () => calendarVisibleWorkItemIds(board, calendarContext),
    [board, calendarContext]
  );
  const visibleBoardWorkItemIds = useMemo(
    () => filteredWorkItems(board, workViewState).map((item) => item.id),
    [board, workViewState]
  );
  const workspaceContext = mode === "calendar"
    ? {
        surface: "CALENDAR",
        ...calendarContext,
        visibleWorkItemIds: visibleCalendarWorkItemIds.slice(0, 32),
        visibleWorkItemsTruncated: visibleCalendarWorkItemIds.length > 32
      } as const
    : mode === "board"
      ? {
          surface: "BOARD",
          workLens: opsWorkLens(workViewState.lens),
          visibleWorkItemIds: visibleBoardWorkItemIds.slice(0, 32),
          visibleWorkItemsTruncated: visibleBoardWorkItemIds.length > 32,
          focusedWorkItemId: workViewState.focusedWorkItemId && visibleBoardWorkItemIds.includes(workViewState.focusedWorkItemId)
            ? workViewState.focusedWorkItemId
            : null
        } as const
      : { surface: mode === "approvals" ? "APPROVALS" : "OPS_CHAT" } as const;

  const focusComposer = () => {
    workspaceRef.current?.querySelector<HTMLTextAreaElement>(".ops-composer textarea:not([disabled])")?.focus();
  };
  const askAgent = () => {
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      returnFocusRef.current = document.activeElement;
    }
    if (typeof window !== "undefined" && window.matchMedia?.("(max-width: 1000px)").matches && !mobileChatOpen) {
      openMobileChat?.();
      return;
    }
    // WHY [BOL-APP-WORKSPACE-SIDECHAT-001]: a folded side chat has no visible composer to focus; Ask Agent must
    // unfold it first. Falsifier: Ask Agent with the chat folded leaves focus on a hidden textarea.
    if (sideChatCollapsed) {
      setSideChatCollapsed(false);
      window.requestAnimationFrame(focusComposer);
      return;
    }
    focusComposer();
  };
  useEffect(() => {
    if (typeof window === "undefined") return;
    const frame = window.requestAnimationFrame(() => {
      if (mobileChatOpen) {
        focusComposer();
        return;
      }
      const target = returnFocusRef.current;
      if (target && document.contains(target)) target.focus();
      returnFocusRef.current = null;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [mobileChatOpen]);

  const openApprovals = () => {
    // WHY [BOL-TODAY-COUNT-ROUTE-001]: Work counts describe canonical tasks,
    // while Approvals describes prepared effects. Routing task counts to the
    // approval queue made real Work open an unrelated inbox. Only the explicit
    // Approvals control enters that guarded queue; Work counts open Board.
    // Falsifier: a Work count opens Review, or an approval route mutates data.
    const target = ordered[0];
    if (target) setSelectedApprovalId(target.id);
    setMode("approvals");
  };

  const openTaskCount = (route: DailyWorkRoute) => {
    setWorkViewState((current) => workViewStateForDailyRoute(current, route));
    setMode("board");
  };

  const openFollowUps = () => {
    setCalendarContext((current) => calendarContextForFollowUps(current));
    setCalendarSurfaceRevision((current) => current + 1);
    setMode("calendar");
  };

  const openWorkspaceReference = (reference: OpsAssistantWorkspaceReference) => {
    if (reference.surface === "APPROVALS") {
      openApprovals();
      return;
    }
    if (reference.surface === "CALENDAR") {
      if (reference.focusDay) {
        setCalendarContext((current) => calendarViewContextForDay(current, reference.focusDay!));
        setCalendarSurfaceRevision((current) => current + 1);
      }
      setMode("calendar");
      return;
    }
    setWorkViewState((current) => ({
      ...workViewStateForDailyRoute(current, "TASKS"),
      lens: workLensFromReference(reference.workLens)
    }));
    setMode("board");
  };

  const summarizeWorkspaceReference = (
    reference: OpsAssistantWorkspaceReference,
    workItemIds: readonly string[]
  ): OpsWorkspaceReferenceSummary => opsWorkspaceReferenceSummary({
    board,
    calendarContext,
    approvalCount: ordered.length,
    reference,
    workItemIds
  });

  const updateWorkMutationFeedback = (update: WorkMutationFeedbackUpdate) => {
    setWorkMutationFeedback((current) => applyWorkMutationFeedbackUpdate(current, update));
  };

  useEffect(() => {
    setWorkMutationFeedback((current) => observeWorkMutationFeedback(current, board.workItems));
  }, [board.workItems]);

  useEffect(() => {
    const terminalOperationIds = new Set(workMutationFeedback
      .filter((entry) => entry.stage === "DONE" || entry.stage === "FAILED")
      .map((entry) => entry.operationId));
    if (terminalOperationIds.size === 0) return;
    const timer = window.setTimeout(() => {
      setWorkMutationFeedback((current) => current.filter((entry) => !terminalOperationIds.has(entry.operationId)));
    }, 2_400);
    return () => window.clearTimeout(timer);
  }, [workMutationFeedback]);

  useEffect(() => {
    // WHY [BOL-APPROVAL-SELECTION-001]: approving/rejecting removes the exact
    // card from the live queue. Keep the next valid card selected without
    // mutating, snoozing, or reordering any other approval. Falsifier: a queue
    // refresh shows a blank card while reviewable items remain.
    if (!ordered.length) setSelectedApprovalId(null);
    else if (!ordered.some((unit) => unit.id === selectedApprovalId)) setSelectedApprovalId(ordered[0]!.id);
  }, [ordered, selectedApprovalId]);

  const renderProposal = (
    id: string,
    settlePublicTurn?: () => Promise<void>,
    publicProposals: readonly OpsPublicProposalProjection[] = []
  ) => {
    const knownGroup = [...knownBundleGroupsRef.current.values()]
      .find((group) => group.proposalIds.includes(id));
    const proposalIds = knownGroup?.proposalIds ?? [id];
    const relevantPublicProposals = publicProposals.filter((proposal) => proposalIds.includes(proposal.id));
    const hasPublicProjection = relevantPublicProposals.length > 0;
    const terminalReceiptSet = terminalPublicProposalReceiptSet(proposalIds, relevantPublicProposals);
    if (terminalReceiptSet) return <PublicProposalTerminalReceiptUnit projections={terminalReceiptSet} />;
    const pendingUnit = pendingUnitByProposalId.get(id);
    if (pendingUnit && (!hasPublicProjection || proposalUnitMatchesPublicProjection(
      pendingUnit,
      proposalIds,
      relevantPublicProposals
    ))) {
      return <ApprovalUnitCard
        key={pendingUnit.id}
        unit={pendingUnit}
        board={board}
        queue={queue}
        embedded
        approveBundle={(bundleId, approvals) => api.approveProposalBundle(bundleId, approvals)}
        {...(settlePublicTurn ? { settlePublicTurn } : {})}
      />;
    }
    const outcomeUnit = historyUnitByProposalId.get(id);
    if (outcomeUnit && (!hasPublicProjection || proposalUnitMatchesPublicProjection(
      outcomeUnit,
      proposalIds,
      relevantPublicProposals
    ))) return <ApprovalUnitOutcomeCard
      unit={outcomeUnit}
      publicProposals={relevantPublicProposals}
      actions={proposalOutcomeActions(queue, settlePublicTurn)}
    />;
    if (hasPublicProjection) return <ExactProposalUnit
      proposalIds={proposalIds}
      publicProposals={relevantPublicProposals}
      board={board}
      queue={queue}
      {...(settlePublicTurn ? { settlePublicTurn } : {})}
    />;
    // WHY [BOL-OPS-INLINE-TERMINAL-BRIDGE-001]: Reject/approve removes the
    // active card before the canonical history refresh arrives. This bridge is
    // deliberately neutral: calling it approved falsely describes rejection,
    // while calling it ready falsely offers a second decision. Falsifier: the
    // inline card says approved/sent/rejected before its terminal ledger loads.
    return (
      <div className="ops-proposal-status" role="status">
        <ShieldCheck size={17} aria-hidden="true" />
        <span><strong>{OPS_PROPOSAL_TRANSITION_STATUS.title}</strong><small>{OPS_PROPOSAL_TRANSITION_STATUS.detail}</small></span>
      </div>
    );
  };

  const workspaceSurface = mode === "calendar" ? (
    <WorkspaceSurface title="Calendar" workspaceWidth={workspaceWidth} setWorkspaceWidth={setWorkspaceWidth}>
      <CalendarView key={calendarSurfaceRevision} embedded board={board} openWork={openWork} addWorkForDay={addWorkForDay} initialContext={calendarContext} onContextChange={setCalendarContext} />
    </WorkspaceSurface>
  ) : mode === "board" ? (
    <WorkspaceSurface title="Tasks" workspaceWidth={workspaceWidth} setWorkspaceWidth={setWorkspaceWidth}>
      <WorkView embedded toolbarSlot={desktopWorkspace && !chatFocused ? toolbarSlot : null} board={board} openWork={openWork} moveWork={moveWork} addWork={addWork} agentQueue={queue} viewState={workViewState} setViewState={setWorkViewState} mutationFeedback={workMutationFeedback} canArchive={canArchiveWork} />
    </WorkspaceSurface>
  ) : mode === "approvals" ? (
    (inlineProposalIds: ReadonlySet<string>) => {
      const visibleUnits = approvalUnitsWithoutInlineOwner(ordered, inlineProposalIds);
      return <WorkspaceSurface title="Approvals" workspaceWidth={workspaceWidth} setWorkspaceWidth={setWorkspaceWidth}>
        <ApprovalInbox
          units={visibleUnits}
          selectedId={selectedApprovalId}
          select={setSelectedApprovalId}
          loadState={queue.loadState}
          refresh={queue.refresh}
          board={board}
          queue={queue}
        />
      </WorkspaceSurface>;
    }
  ) : null;

  // WHY [BOL-APP-WORKSPACE-TOOLBAR-001]: title, actions, counts strip + Summary and Private share ONE row at the
  // top of the open panel in every view (the counts strip used to fall to the bottom of split views as an
  // auto-placed grid row). Chat keeps the strip in every Chat state (BOL-SUMMARY-JUMP-001), including Ask Agent
  // from a tool on phones, where the tool is hidden and its title/actions would be wrong.
  const toolTitle = mode === "calendar" ? "Calendar" : mode === "board" ? "Tasks" : mode === "approvals" ? "Approvals" : "Ops Chat";
  const toolEyebrow = mode === "calendar" ? "Timing" : mode === "board" ? "Work" : mode === "approvals" ? "Manual mode" : undefined;
  const toolActions = chatFocused ? null : <>
    {mode === "calendar" || mode === "board" ? <button className="primary-button bol-toolbar__add" type="button" onClick={addWork}><Plus size={16} aria-hidden="true" /><span>Add task</span></button> : null}
    <button className="ops-surface__back" type="button" onClick={askAgent} aria-label={`Ask Agent from ${toolTitle}`}>{/* WHY [BOL-ASK-AGENT-ICON-001] (human 2026-09-26: "I dont like this arrow for agent … its confusion"): a left arrow read as "go back"; the Ops Chat bubble says "talk to the agent". */}<MessageSquareText size={17} aria-hidden="true" /><span>Ask Agent</span></button>
  </>;
  const toolbar = <WorkspaceToolbar
    title={chatFocused ? "Ops Chat" : toolTitle}
    eyebrow={chatFocused ? undefined : toolEyebrow}
    actions={toolActions}
    viewSlot={mode === "board" && !chatFocused && desktopWorkspace ? setToolbarSlot : undefined}
    sideChat={chatFocused ? undefined : { collapsed: sideChatCollapsed, toggle: () => setSideChatCollapsed(!sideChatCollapsed) }}
    counts={<DailyActionStrip board={board} approvalCount={ordered.length} loadState={queue.loadState} openTasks={() => openTaskCount("TASKS")} openPending={() => openTaskCount("PENDING")} openApprovals={openApprovals} openFollowUps={openFollowUps} />}
  />;

  return (
    <div ref={workspaceRef} className={`view-stack view-stack--ops ops-workspace${chatFocused ? " ops-workspace--chat-focused" : ""}${!chatFocused && sideChatCollapsed ? " ops-workspace--side-chat-collapsed" : ""}`}>
      <h1 className="sr-only">Ops Agent</h1>
      {/* WHY [BOL-UNIFIED-OPS-WORKSPACE-001]: the selected operational view
          owns most of this single workspace while the mounted conversation and
          composer remain available. Board reveals flow/owners/blockers;
          Calendar reveals dates/load; neither owns another database.
          Falsifier: the tool is cramped, chat disappears, or views disagree. */}
      <section className="ops-workspace__chat" aria-label="Ops Agent conversation">
        <ProposalDraftStateProvider scope={historyScope}>
          <OpsAssistantPanel
          embedded
          historyScope={historyScope}
          publicReleaseId={publicReleaseId}
          // WHY [BOL-TODAY-WELCOME-TOP-002]: workspace switches may settle a
          // tool conversation, but Today entry must start at its welcome—not
          // treat the default mode itself as new chat and scroll to the bottom.
          conversationPriorityKey={mode === "today" ? null : mode}
          notify={queue.notify}
          afterBoardChange={queue.afterBoardChange}
          openWork={(id) => {
            const item = board.workItems.find((candidate) => candidate.id === id);
            if (item) {
              setWorkViewState((current) => ({ ...current, focusedWorkItemId: item.id }));
              openWork(item);
            }
          }}
          workTitle={(id) => board.workItems.find((candidate) => candidate.id === id)?.title ?? "task"}
          // WHY [BOL-APPROVALS-SINGLE-DRAFT-001]: Chat and the standalone
          // Approvals workspace are two presentation owners for one exact
          // decision unit. Manual mode transfers the interactive card to the
          // canonical queue, while the scoped provider above retains unsaved
          // effect/message/schedule/route edits and in-flight progress across
          // the unmount. Other modes keep Chat's inline owner and dedup path.
          // Falsifier: switching with an unsaved edit resets it, exposes a
          // second approval attempt, or leaves the badge/card count unequal.
          {...(mode === "approvals" ? {} : { renderProposal })}
          proposalRenderGroups={proposalRenderGroups}
          currentReviewProposalIds={currentReviewProposalIds}
          unmaterializedWorkProposalIds={unmaterializedWorkProposalIds}
          currentWorkItemIds={currentWorkItemIds}
          mediaOwner={mediaOwner}
          openWorkspace={openWorkspaceReference}
          workspaceReferenceSummary={summarizeWorkspaceReference}
          workspaceContext={workspaceContext}
          privateMode={privateMode}
          {...(onVoicePresentationPrivacyLockChange
            ? { onVoicePresentationPrivacyLockChange }
            : {})}
          onWorkMutationFeedback={updateWorkMutationFeedback}
          openConversations={openConversations}
          workspaceSurface={workspaceSurface}
          // WHY [BOL-SUMMARY-JUMP-001]: the human (2026-09-15) expects the counts bar and its
          // Summary button in every Chat state; it used to vanish when Chat opened from a
          // tool (Calendar → Ask Agent kept the tool as the selected section).
          // WHY [BOL-APPROVAL-COUNT-CANONICAL-001]: sidebar and daily-strip
          // badges represent the full pending canonical queue, independent of
          // which surface currently owns the interactive card. Falsifier: an
          // inline Chat owner changes the count without a queue revision.
          // WHY [BOL-APP-WORKSPACE-TOOLBAR-001]: the counts strip now rides inside the view's one toolbar row.
          conversationHeader={() => toolbar}
          // WHY [BOL-CHAT-WHATSAPP-STATUS-865]: connection guidance belongs after
          // messages in the existing scroll area, never in persisted transcript,
          // composer or Voice. App owns status and navigation; no second poll,
          // send gate or scroll command. Confirmed CONNECTED removes it in place.
          afterConversation={<WhatsAppChatStatus status={whatsAppConnectionStatus} openWhatsApp={openWhatsApp} />}
          attachmentTools={<MeetingCapture notify={queue.notify} afterReady={queue.afterBoardChange} mediaOwner={mediaOwner} />}
          welcomeOnEntry={mode === "today"}
          />
        </ProposalDraftStateProvider>
      </section>
    </div>
  );
}

export function proposalUnitMatchesPublicProjection(
  unit: ApprovalUnit,
  proposalIds: readonly string[],
  publicProposals: readonly OpsPublicProposalProjection[]
): boolean {
  if (!proposalIds.every((id) => unit.proposals.some((proposal) => proposal.id === id))) return false;
  return publicProposals.every((projection) => {
    const proposal = unit.proposals.find((candidate) => candidate.id === projection.id);
    return proposal !== undefined && proposalMatchesPublicProjection(proposal, projection);
  });
}

export function terminalPublicProposalReceiptSet(
  proposalIds: readonly string[],
  publicProposals: readonly OpsPublicProposalProjection[]
): readonly OpsPublicProposalProjection[] | null {
  if (proposalIds.length === 0 || publicProposals.length !== proposalIds.length) return null;
  const byId = new Map(publicProposals.map((proposal) => [proposal.id, proposal]));
  if (byId.size !== proposalIds.length) return null;
  const ordered = proposalIds.map((id) => byId.get(id));
  if (ordered.some((proposal) => proposal === undefined)
    || !ordered.every((proposal) => proposal !== undefined && hasTerminalPublicEffectReceipt(proposal))) {
    return null;
  }
  // WHY [BOL-J2-PUBLIC-TERMINAL-CARD-001]: a release-valid public turn is the
  // immutable owner of its terminal effect/provider receipts. Resolve that
  // complete exact-ID set before consulting bounded /review state; the latter
  // remains only the editable/nonterminal detail owner. Falsifier: a terminal
  // receipt waits on GET /review, or an incomplete linked set renders as whole.
  return ordered as readonly OpsPublicProposalProjection[];
}

function proposalMatchesPublicProjection(
  proposal: AgentProposal,
  projection: OpsPublicProposalProjection
): boolean {
  return proposal.state === projection.state
    && proposal.version === projection.version
    && proposal.currentRevision === projection.currentRevision
    && proposal.approvedRevision === projection.approvedRevision;
}

async function loadExactApprovalUnit(proposalIds: readonly string[]): Promise<ApprovalUnit> {
  const uniqueIds = [...new Set(proposalIds)];
  if (uniqueIds.length === 0) throw new Error("The exact approval identity is missing.");
  const proposals = await Promise.all(uniqueIds.map((id) => api.proposal(id)));
  const unit = approvalUnits(proposals).find((candidate) =>
    candidate.proposals.some((proposal) => proposal.id === uniqueIds[0]));
  if (!unit || uniqueIds.some((id) => !unit.proposals.some((proposal) => proposal.id === id))) {
    throw new Error("The linked approval changed while its exact outcome was loading.");
  }
  return unit;
}

function exactProposalRefreshMatches(
  workspaceId: string,
  proposalIds: readonly string[],
  hint: BoardRefreshHint
): boolean {
  if (hint.workspaceId !== workspaceId) return false;
  if (hint.kind === "READY") return true;
  return hint.event.startsWith("agent_proposal.") && proposalIds.includes(hint.entityId);
}

export function connectExactProposalRefresh(
  workspaceId: string,
  proposalIds: readonly string[],
  refresh: () => void
): () => void {
  return subscribeBoardRefreshHints((hint) => {
    if (exactProposalRefreshMatches(workspaceId, proposalIds, hint)) refresh();
  });
}

function ExactProposalUnit({ proposalIds, publicProposals, board, queue, settlePublicTurn }: {
  proposalIds: readonly string[];
  publicProposals: readonly OpsPublicProposalProjection[];
  board: BoardSnapshot;
  queue: AgentWorkQueue;
  settlePublicTurn?: () => Promise<void>;
}) {
  const loadKey = JSON.stringify({
    proposalIds,
    publicProposals: publicProposals.map((proposal) => ({
      id: proposal.id,
      state: proposal.state,
      version: proposal.version,
      currentRevision: proposal.currentRevision,
      approvedRevision: proposal.approvedRevision
    }))
  });
  const [snapshot, setSnapshot] = useState<{
    key: string;
    state: "LOADING" | "READY" | "FAILED";
    unit: ApprovalUnit | null;
  }>({ key: loadKey, state: "LOADING", unit: null });
  const mountedRef = useRef(true);
  const loadGenerationRef = useRef(0);
  const initialLoadRef = useRef<{ key: string; load: Promise<ApprovalUnit> } | null>(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  useEffect(() => {
    let active = true;
    const generation = ++loadGenerationRef.current;
    if (snapshot.unit && proposalUnitMatchesPublicProjection(
      snapshot.unit,
      proposalIds,
      publicProposals
    )) {
      setSnapshot({ key: loadKey, state: "READY", unit: snapshot.unit });
      return () => { active = false; };
    }
    setSnapshot({ key: loadKey, state: "LOADING", unit: null });
    // WHY [BOL-OPS-EXACT-OUTCOME-WEB-001]: Review history intentionally keeps
    // only 20 rows and may omit either half of a just-approved Task + Message.
    // A release-valid public projection supplies the bounded exact IDs. Read once
    // for that projection, then only again when the existing workspace SSE names
    // one of those IDs or reconnects; never poll/retry transport. The full rows
    // restore effect, provider invocation, and safe edit/cancel CAS while the
    // immutable projection remains approval-receipt authority. Falsifier: omitted
    // history or a terminal after the 24 s waiter needs reload, or an unrelated/
    // guessed/unprojected ID causes an exact GET.
    const load = initialLoadRef.current?.key === loadKey
      ? initialLoadRef.current.load
      : loadExactApprovalUnit(proposalIds);
    initialLoadRef.current = { key: loadKey, load };
    void load.then((unit) => {
      if (active && generation === loadGenerationRef.current) {
        setSnapshot({ key: loadKey, state: "READY", unit });
      }
    }).catch(() => {
      if (active && generation === loadGenerationRef.current) {
        setSnapshot({ key: loadKey, state: "FAILED", unit: null });
      }
    });
    return () => { active = false; };
  }, [loadKey]);

  useEffect(() => connectExactProposalRefresh(board.workspace.id, proposalIds, () => {
    const generation = ++loadGenerationRef.current;
    setSnapshot({ key: loadKey, state: "LOADING", unit: null });
    void loadExactApprovalUnit(proposalIds).then((unit) => {
      if (mountedRef.current && generation === loadGenerationRef.current) {
        setSnapshot({ key: loadKey, state: "READY", unit });
      }
    }).catch(() => {
      if (mountedRef.current && generation === loadGenerationRef.current) {
        setSnapshot({ key: loadKey, state: "FAILED", unit: null });
      }
    });
  }), [board.workspace.id, loadKey]);

  const actions: ProposalOutcomeActions = {
    notify: queue.notify,
    afterMutation: async () => {
      const generation = ++loadGenerationRef.current;
      const exactLoad = loadExactApprovalUnit(proposalIds);
      const refreshes: Promise<unknown>[] = [queue.afterBoardChange()];
      if (settlePublicTurn) refreshes.push(settlePublicTurn());
      const refreshResultsLoad = Promise.allSettled(refreshes);
      const exactResult = await exactLoad.then(
        (value) => ({ status: "fulfilled" as const, value }),
        (reason: unknown) => ({ status: "rejected" as const, reason })
      );
      const refreshResults = await refreshResultsLoad;
      if (exactResult.status === "fulfilled"
        && mountedRef.current
        && generation === loadGenerationRef.current) {
        setSnapshot({ key: loadKey, state: "READY", unit: exactResult.value });
      }
      if (exactResult.status === "rejected" || refreshResults.some((result) => result.status === "rejected")) {
        throw new Error("The latest linked outcome could not refresh yet.");
      }
    }
  };

  if (snapshot.key !== loadKey || snapshot.state === "LOADING") return (
    <div className="ops-proposal-status" role="status">
      <ShieldCheck size={17} aria-hidden="true" />
      <span><strong>{OPS_PROPOSAL_TRANSITION_STATUS.title}</strong><small>Loading this approval.</small></span>
    </div>
  );
  if (snapshot.state === "FAILED" || !snapshot.unit) return (
    <div className="ops-proposal-status" role="alert">
      <ShieldCheck size={17} aria-hidden="true" />
      <span><strong>Couldn’t load this approval.</strong><small>This screen did not change it.</small></span>
    </div>
  );
  const unit = snapshot.unit;
  if (unit.proposals.every((proposal) => proposal.state === "READY_FOR_REVIEW")) {
    if (unit.complete) return (
      <ApprovalUnitCard
        key={`${unit.id}:${unit.proposals.map((proposal) => proposal.version).join(":")}`}
        unit={unit}
        board={board}
        queue={queue}
        embedded
        approveBundle={(bundleId, approvals) => api.approveProposalBundle(bundleId, approvals)}
        {...(settlePublicTurn ? { settlePublicTurn } : {})}
      />
    );
    return <div className="ops-proposal-status" role="status">
      <ShieldCheck size={17} aria-hidden="true" />
      <span><strong>Returned to Approvals.</strong><small>Reload Approvals to review the linked Task + Message together.</small></span>
    </div>;
  }
  const currentPublicProposals = publicProposals.filter((projection) => {
    const proposal = unit.proposals.find((candidate) => candidate.id === projection.id);
    return proposal !== undefined && proposalMatchesPublicProjection(proposal, projection);
  });
  return <ApprovalUnitOutcomeCard unit={unit} publicProposals={currentPublicProposals} actions={actions} />;
}

function proposalOutcomeActions(
  queue: AgentWorkQueue,
  settlePublicTurn?: () => Promise<void>
): ProposalOutcomeActions {
  return {
    notify: queue.notify,
    afterMutation: async () => {
      const refreshes: Promise<unknown>[] = [queue.afterBoardChange()];
      if (settlePublicTurn) refreshes.push(settlePublicTurn());
      const results = await Promise.allSettled(refreshes);
      if (results.some((result) => result.status === "rejected")) {
        throw new Error("The latest linked outcome could not refresh yet.");
      }
    }
  };
}

export function calendarVisibleWorkItemIds(board: BoardSnapshot, context: CalendarViewContext): string[] {
  const ids = calendarEvents(board, context.calendarScope)
    .filter((event) => event.extendedProps.dayKey >= context.visibleStartDay
      && event.extendedProps.dayKey < context.visibleEndDayExclusive)
    .map((event) => event.extendedProps.workId);
  return [...new Set([
    ...(context.focusedWorkItemId ? [context.focusedWorkItemId] : []),
    ...ids
  ])];
}

export function opsWorkspaceReferenceSummary(input: {
  board: BoardSnapshot;
  calendarContext: CalendarViewContext;
  approvalCount: number;
  reference: OpsAssistantWorkspaceReference;
  workItemIds: readonly string[];
}): OpsWorkspaceReferenceSummary {
  if (input.reference.surface === "APPROVALS") {
    return {
      eyebrow: "Live workspace",
      title: "Approvals",
      detail: `${input.approvalCount} ${input.approvalCount === 1 ? "approval" : "approvals"} ready in the shared queue.`
    };
  }
  const referencedIds = new Set(input.workItemIds);
  if (input.reference.surface === "CALENDAR") {
    const dayKey = input.reference.focusDay ?? input.calendarContext.selectedDay;
    const entries = calendarEventsForDay(
      calendarEvents(input.board, input.calendarContext.calendarScope),
      dayKey
    ).filter((event) => referencedIds.size === 0 || referencedIds.has(event.extendedProps.workId));
    const dayLabel = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" })
      .format(new Date(`${dayKey}T12:00:00`));
    return {
      eyebrow: "Live Calendar",
      title: dayLabel,
      detail: `${entries.length} structured ${entries.length === 1 ? "entry" : "entries"} from the shared Work store.`
    };
  }
  const liveItems = input.board.workItems.filter((item) =>
    (item.workKind ?? "TASK") === "TASK"
    && item.status !== "ARCHIVED"
    && (referencedIds.size === 0 || referencedIds.has(item.id)));
  const title = input.reference.workLens === "TIMELINE"
    ? "Timeline"
    : input.reference.workLens === "TABLE"
      ? "Task table"
      : "Board";
  return {
    eyebrow: "Live Tasks",
    title,
    detail: `${liveItems.length} ${liveItems.length === 1 ? "task" : "tasks"} from the shared Work store.`
  };
}

export function todayBriefCounts(board: BoardSnapshot, approvalCount: number, now = new Date()) {
  const today = localDayKey(now, board.workspace.timezone);
  const tasks = board.workItems.filter((item) => (item.workKind ?? "TASK") === "TASK");
  const active = tasks.filter((item) => !["DONE", "ARCHIVED"].includes(item.status));
  const completedToday = board.dailyWorkSummary?.dayKey === today
    ? new Set(board.dailyWorkSummary.completedWorkItemIds)
    : new Set<string>();
  return {
    approvals: approvalCount,
    tasks: active.length,
    pending: active.filter((item) => item.approvalStatus === "INTERNAL_PENDING" || item.approvalStatus === "CLIENT_PENDING").length,
    followUps: calendarEvents(board, "FOLLOW_UPS", now).length,
    open: active.length,
    attention: active.filter((item) => needsAttention(item, today, board.workspace.timezone)).length,
    done: tasks.filter((item) => item.status === "DONE" && completedToday.has(item.id)).length
  };
}

export function todayBriefSummary(counts: ReturnType<typeof todayBriefCounts>, loadState: AgentWorkQueue["loadState"]): string {
  if (loadState !== "READY") return `${counts.open} open; ${counts.attention} need attention. Approvals are refreshing.`;
  if (counts.open === 0 && counts.approvals === 0) return counts.done ? `${counts.done} completed today. Nothing needs attention.` : "Nothing needs attention yet.";
  const done = counts.done ? `${counts.done} completed today. ` : "";
  return `${done}${counts.open} open; ${counts.attention} need attention; ${counts.approvals} ready to review.`;
}

export function todayOperationalSummary(
  board: BoardSnapshot,
  approvalCount: number,
  loadState: AgentWorkQueue["loadState"],
  now = new Date()
): string {
  const today = localDayKey(now, board.workspace.timezone);
  const active = board.workItems.filter((item) => (item.workKind ?? "TASK") === "TASK" && !["DONE", "ARCHIVED"].includes(item.status));
  const attention = active.filter((item) => needsAttention(item, today, board.workspace.timezone));
  const ordered = [...active].sort((left, right) =>
    Number(needsAttention(right, today, board.workspace.timezone)) - Number(needsAttention(left, today, board.workspace.timezone))
    || workSummaryTime(left) - workSummaryTime(right)
    || left.title.localeCompare(right.title));
  const visible = ordered.slice(0, 2).map((item) => {
    const owner = memberName(board, item.assigneeId);
    return owner === "Unassigned" ? item.title : `${item.title} (${owner})`;
  });
  const hidden = active.length - visible.length;
  const open = active.length
    ? `${active.length} open: ${naturalList(visible)}${hidden ? `, plus ${hidden} more` : ""}.`
    : "No open tasks.";
  const urgent = attention.length === 0
    ? ""
    : attention.length === active.length
      ? active.length === 1 ? " It needs attention now." : active.length === 2 ? " Both need attention now." : " All need attention now."
      : ` ${attention.length} ${attention.length === 1 ? "needs" : "need"} attention now.`;
  const counts = todayBriefCounts(board, approvalCount, now);
  const completed = counts.done ? ` ${counts.done} completed today.` : "";
  const approvals = loadState === "READY"
    ? approvalCount ? ` ${approvalCount} ${approvalCount === 1 ? "approval needs" : "approvals need"} review.` : ""
    : " Approvals are refreshing.";
  // WHY [BOL-TODAY-REAL-SUMMARY-001]: counts alone force the operator to open every
  // task to learn what the day means. Name the first two canonical records and
  // state the subset relationship explicitly; “1 more” hid the exact second
  // task and made Open/Due look like four different records. Falsifier: the
  // visible names do not explain the count, or completed work uses updatedAt.
  return `${open}${urgent}${completed}${approvals}`;
}

function needsAttention(item: WorkItem, today: string, timezone: string): boolean {
  if (item.status === "BLOCKED") return true;
  return [item.dueAt, item.nextCheckpointAt].some((value) => {
    if (!value) return false;
    const date = new Date(value);
    return Number.isFinite(date.getTime()) && localDayKey(date, timezone) <= today;
  });
}

function workSummaryTime(item: WorkItem): number {
  const values = [item.dueAt, item.nextCheckpointAt]
    .filter((value): value is string => Boolean(value))
    .map((value) => Date.parse(value))
    .filter(Number.isFinite);
  return values.length ? Math.min(...values) : Number.MAX_SAFE_INTEGER;
}

function naturalList(values: readonly string[]): string {
  if (values.length < 2) return values[0] ?? "nothing";
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, and ${values.at(-1)}`;
}

export type DailyWorkRoute = "TASKS" | "PENDING";

export function workViewStateForDailyRoute(current: WorkViewState, route: DailyWorkRoute): WorkViewState {
  return {
    ...current,
    query: "",
    assignee: "ALL",
    showCompleted: false,
    approval: route === "PENDING" ? "PENDING" : "ALL",
    focusedWorkItemId: null
  };
}

export function calendarContextForFollowUps(current: CalendarViewContext): CalendarViewContext {
  return { ...current, calendarScope: "FOLLOW_UPS", focusedWorkItemId: null };
}

function opsWorkLens(lens: WorkLens): "BOARD" | "TIMELINE" | "TABLE" {
  return lens === "timeline" ? "TIMELINE" : lens === "table" ? "TABLE" : "BOARD";
}

function workLensFromReference(lens: OpsAssistantWorkspaceReference["workLens"]): WorkLens {
  return lens === "TIMELINE" ? "timeline" : lens === "TABLE" ? "table" : "board";
}

function DailyActionStrip({ board, approvalCount, loadState, openTasks, openPending, openApprovals, openFollowUps }: {
  board: BoardSnapshot;
  approvalCount: number;
  loadState: AgentWorkQueue["loadState"];
  openTasks: () => void;
  openPending: () => void;
  openApprovals: () => void;
  openFollowUps: () => void;
}) {
  const counts = todayBriefCounts(board, approvalCount);
  const [summaryMissing, setSummaryMissing] = useState(false);
  return (
    <section className="ops-daily-strip" aria-label="Current operations summary">
      {/* WHY [BOL-OPS-ONE-ACTION-STRIP-001]: the former briefing rendered the
          same queue as a large card, a second Review button, three boxed
          counters, and persistent navigation. One compact action rail
          preserves every route without consuming the chat. The factual
          sentence that used to sit beside the counts was removed on
          2026-09-15 at the human's direction (BOL-STRIP-COUNTS-ONLY-001): on
          desktop it duplicated the four counts, on phones it cost the thread
          two lines and flashed while the history hydrated.
          `todayOperationalSummary` stays exported for its tests and a future
          spoken brief. Pending is the exact active Work approvalStatus
          projection; Follow-ups is the exact active structured Calendar
          projection. Falsifier: a count opens a different filter, entry
          duplicates approvals, the composer is no longer available before
          messaging Luna, or the sentence returns. */}
      <nav className="ops-daily-strip__actions" aria-label="Open current work">
        <button type="button" onClick={openTasks} aria-label={`${counts.tasks} active tasks; open Tasks`}><b>{counts.tasks}</b><span>Tasks</span></button>
        <button type="button" onClick={openPending} aria-label={`${counts.pending} active tasks pending approval; open filtered Tasks`}><b>{counts.pending}</b><span>Pending</span></button>
        <button className={counts.approvals > 0 ? "ops-daily-strip__approvals is-attention" : "ops-daily-strip__approvals"} type="button" onClick={openApprovals}>
          <b>{loadState === "READY" ? counts.approvals : "…"}</b><span>Approvals</span>
        </button>
        <button type="button" onClick={openFollowUps} aria-label={`${counts.followUps} active follow-ups; open filtered Calendar`}><b>{counts.followUps}</b><span>Follow-ups</span></button>
      </nav>
      {/* WHY [BOL-SUMMARY-JUMP-001]: one real button outside the label-backed counts
          row (that row is guarded chrome-free) that jumps to the newest summary turn;
          when none exists it says so instead of doing nothing. */}
      <button type="button" className="secondary-button ops-daily-strip__jump" onClick={() => setSummaryMissing(!requestSummaryJump())} aria-label="Jump to the latest summary in the chat" title="Latest summary">
        <FileText size={16} aria-hidden="true" /><span>Summary</span>
      </button>
      {summaryMissing ? <small className="ops-daily-strip__jump-status" role="status">No summary yet</small> : null}
    </section>
  );
}

function localDayKey(value: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(value);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((entry) => entry.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

// WHY [BOL-CHAT-WORKSPACE-RESIZE-001]: desktop needs an adjustable work area,
// but an unconstrained divider can erase the mounted control-plane Chat. These
// bounds retain a 480px tool and 320px Chat, and reclamp on host resize.
// Falsifier: either pane clips/disappears or keyboard cannot move the divider.
export function workspaceSplitBounds(totalWidth: number): { min: number; max: number } {
  const safeWidth = Math.max(1_001, totalWidth);
  const min = Math.max(48, Math.min(62, 480 / safeWidth * 100));
  const max = Math.max(min, Math.min(76, 100 - 320 / safeWidth * 100));
  return { min, max };
}

export function workspaceWidthAfterKey(current: number, key: string, totalWidth: number): number {
  const { min, max } = workspaceSplitBounds(totalWidth);
  if (key === "Home") return min;
  if (key === "End") return max;
  if (key !== "ArrowLeft" && key !== "ArrowRight") return current;
  return Math.min(max, Math.max(min, current + (key === "ArrowRight" ? 2 : -2)));
}

function WorkspaceResizeHandle({ width, setWidth, controlsId, label }: {
  width: number;
  setWidth: (width: number) => void;
  controlsId: string;
  label: string;
}) {
  const handleRef = useRef<HTMLDivElement>(null);
  const [ownerWidth, setOwnerWidth] = useState(1_440);
  const assistant = () => handleRef.current?.closest<HTMLElement>(".ops-assistant") ?? null;
  const widthFromPointer = (clientX: number) => {
    const owner = assistant();
    if (!owner) return;
    const bounds = owner.getBoundingClientRect();
    setOwnerWidth(Math.round(bounds.width));
    const limits = workspaceSplitBounds(bounds.width);
    setWidth(Math.min(limits.max, Math.max(limits.min, (clientX - bounds.left) / bounds.width * 100)));
  };
  useEffect(() => {
    const owner = assistant();
    if (!owner) return;
    const applyWidth = () => {
      const bounds = owner.getBoundingClientRect();
      setOwnerWidth(Math.round(bounds.width));
      const limits = workspaceSplitBounds(bounds.width);
      const next = Math.min(limits.max, Math.max(limits.min, width));
      owner.style.setProperty("--ops-workspace-width", `${next}%`);
      if (next !== width) setWidth(next);
    };
    applyWidth();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(applyWidth);
    observer.observe(owner);
    return () => observer.disconnect();
  }, [setWidth, width]);
  const accessibleBounds = workspaceSplitBounds(ownerWidth);
  return (
    <div
      ref={handleRef}
      className="ops-workspace-resizer"
      role="separator"
      tabIndex={0}
      aria-label={`${label} width`}
      aria-controls={controlsId}
      aria-orientation="vertical"
      aria-valuemin={Math.round(accessibleBounds.min)}
      aria-valuemax={Math.round(accessibleBounds.max)}
      aria-valuenow={Math.round(width)}
      aria-valuetext={`${Math.round(width)}% of the split for ${label}`}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        widthFromPointer(event.clientX);
      }}
      onPointerMove={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) widthFromPointer(event.clientX);
      }}
      onPointerUp={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onKeyDown={(event) => {
        const ownerWidth = assistant()?.getBoundingClientRect().width ?? 1_440;
        const next = workspaceWidthAfterKey(width, event.key, ownerWidth);
        if (next === width) return;
        event.preventDefault();
        setWidth(next);
      }}
    ><span aria-hidden="true" /></div>
  );
}

// WHY [BOL-APP-WORKSPACE-TOOLBAR-001]: the surface no longer repeats its title/actions ("where is says tasks
// twice"); the view's single toolbar row above the split owns them. The region keeps its name and resizer.
function WorkspaceSurface({ title, workspaceWidth, setWorkspaceWidth, children }: { title: string; workspaceWidth: number; setWorkspaceWidth: (width: number) => void; children: ReactNode }) {
  const surfaceId = `ops-surface-${title.toLowerCase().replace(/[^a-z0-9]+/gu, "-")}`;
  return (
    <section id={surfaceId} className="ops-surface" aria-label={`${title} workspace`}>
      <div className="ops-surface__body">{children}</div>
      <WorkspaceResizeHandle width={workspaceWidth} setWidth={setWorkspaceWidth} controlsId={surfaceId} label={`${title} workspace`} />
    </section>
  );
}

function ApprovalInbox({ units, selectedId, select, loadState, refresh, board, queue }: {
  units: readonly ApprovalUnit[];
  selectedId: string | null;
  select: (id: string) => void;
  loadState: AgentWorkQueue["loadState"];
  refresh: AgentWorkQueue["refresh"];
  board: BoardSnapshot;
  queue: AgentWorkQueue;
}) {
  if (loadState === "LOADING" && units.length === 0) {
    return <section className="ops-inbox ops-inbox--state" aria-label="Approvals"><ShieldCheck size={18} aria-hidden="true" /><span><strong>Checking for approvals</strong></span></section>;
  }
  if (loadState === "STALE" && units.length === 0) {
    return <section className="ops-inbox ops-inbox--state" role="alert"><span><strong>Approvals could not refresh</strong></span>{refresh ? <button className="secondary-button" type="button" onClick={() => void refresh()}>Try again</button> : null}</section>;
  }
  const selectedIndex = Math.max(0, units.findIndex((unit) => unit.id === selectedId));
  const selected = units[selectedIndex] ?? null;
  if (!selected) {
    return <section className="ops-inbox ops-inbox--state" aria-label="Approvals"><CheckCircle2 size={18} aria-hidden="true" /><span><strong>No approvals waiting</strong></span></section>;
  }

  const previous = () => {
    const target = units[selectedIndex - 1];
    if (target) select(target.id);
  };
  const next = () => {
    const target = units[selectedIndex + 1];
    if (target) select(target.id);
  };
  return (
    <section className="ops-inbox" aria-label={`${units.length} ${units.length === 1 ? "approval" : "approvals"}`}>
      <header className="ops-inbox__header">
        {/* WHY [BOL-APPROVAL-NO-SKIP-001]: Previous/Next already browse without
            mutation. A third Skip control duplicated navigation and looked like
            a decision. Reject is the only removal action and is preserved in
            durable history. Falsifier: browsing changes queue state, or a
            rejected card remains in the active stack. */}
        <button type="button" disabled={selectedIndex === 0} onClick={previous} aria-label="Previous approval"><ArrowLeft size={17} aria-hidden="true" /><span>Previous</span></button>
        <span><strong>{selectedIndex + 1} of {units.length}</strong><small>{proposalSourceSummary(selected.proposals[0]!)}</small></span>
        <button type="button" disabled={selectedIndex === units.length - 1} onClick={next} aria-label="Next approval"><span>Next</span><ArrowRight size={17} aria-hidden="true" /></button>
      </header>
      {loadState === "STALE" ? <p className="ops-inbox__stale" role="status">Showing the last loaded approval.</p> : null}
      {/* WHY [BOL-APPROVAL-CARD-IDENTITY-001]: rejection/edit controls belong
          to one exact proposal. React otherwise reuses this card when Reject
          advances the queue, leaving Confirm reject open on the next real
          action. Key by durable proposal identity so browsing or removal can
          never carry transient authority into another approval. Falsifier:
          reject one card and the next card still shows its rejection form. */}
      <ApprovalUnitCard
        key={selected.id}
        unit={selected}
        board={board}
        queue={queue}
        embedded
        approveBundle={(bundleId, approvals) => api.approveProposalBundle(bundleId, approvals)}
      />
    </section>
  );
}

// WHY [BOL-OPS-INLINE-ORDER-001]: one-at-a-time review still needs a stable,
// safety-aware next item. Executable work wins, then identity decisions, then
// honest waits; expiry and IDs keep refreshes deterministic.
export function orderAgentProposals(proposals: readonly AgentProposal[]): AgentProposal[] {
  const attentionRank = { READY: 0, IDENTITY_DECISION: 1, WAITING: 2 } as const;
  const actionRank: Readonly<Record<AgentProposal["type"], number>> = {
    CLARIFICATION_MESSAGE: 0,
    ANSWER_MESSAGE: 1,
    FOLLOWUP_MESSAGE: 2,
    ESCALATION_MESSAGE: 2,
    NEW_WORK: 3,
    BOARD_UPDATE: 4,
    BUSINESS_RULE_UPDATE: 5
  };
  const riskRank = { HIGH: 0, MEDIUM: 1, LOW: 2 } as const;
  const time = (value: string | undefined) => {
    const parsed = value ? Date.parse(value) : Number.NaN;
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
  };
  return [...proposals].sort((left, right) =>
    attentionRank[proposalAttentionKind(left)] - attentionRank[proposalAttentionKind(right)]
    || actionRank[left.type] - actionRank[right.type]
    || time(left.expiresAt) - time(right.expiresAt)
    || riskRank[left.risk] - riskRank[right.risk]
    || time(left.createdAt) - time(right.createdAt)
    || left.id.localeCompare(right.id));
}

export function orderAgentProposalUnits(proposals: readonly AgentProposal[]): ApprovalUnit[] {
  return approvalUnits(orderAgentProposals(proposals));
}

export function approvalUnitsWithoutInlineOwner(
  units: readonly ApprovalUnit[],
  inlineProposalIds: ReadonlySet<string>
): ApprovalUnit[] {
  return units.filter((unit) => unit.proposals.every((proposal) => !inlineProposalIds.has(proposal.id)));
}

function approvalUnitByProposalId(units: readonly ApprovalUnit[]): ReadonlyMap<string, ApprovalUnit> {
  const byProposalId = new Map<string, ApprovalUnit>();
  for (const unit of units) {
    for (const proposal of unit.proposals) byProposalId.set(proposal.id, unit);
  }
  return byProposalId;
}

export function proposalSourceSummary(proposal: AgentProposal): string {
  const source = proposal.evidence[0];
  if (source?.conversationLabel && source.senderLabel) return `${source.conversationLabel} · ${source.senderLabel}`;
  if (source?.conversationLabel) return source.conversationLabel;
  if (proposal.destinationLabel) return proposal.destinationLabel;
  return proposal.whyNow;
}
